import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { child as childApi } from "@/lib/api";
import { Home, Activity, BarChart3, BookOpen, Settings as SettingsIcon, Shield, Eye, ChevronDown, ChevronRight, MoreHorizontal, CalendarCheck2, GraduationCap, TrendingUp, Crown, Star, LayoutGrid, ChevronsLeft, ChevronsRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import BrandedLoader from "@/components/BrandedLoader";
import useRouteTransition from "@/lib/useRouteTransition";
import AccountDrawer from "@/components/AccountDrawer";
import NotificationsBell from "@/components/NotificationsBell";
import LogoutOverlay from "@/components/LogoutOverlay";
import CommandPaletteTrigger from "@/components/CommandPaletteTrigger";
import useAppReady from "@/lib/useAppReady";
import useDashboardPrefetch from "@/lib/useDashboardPrefetch";
import { setCached, getCached } from "@/lib/cache";

const LOGO = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

export default function AppShell() {
  useAppReady();
  const { user, logout, impersonatedBy, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navigating = useRouteTransition();
  const [child, setChild] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Drives the full-screen logout animation. Set true the moment the
  // parent taps "Log out"; the actual auth-context teardown + redirect
  // run a frame later so the overlay paints first and the dashboard
  // doesn't flash an unauthenticated state.
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    childApi.get().then((c) => { setChild(c); setCached("child", c); }).catch(() => {});
  }, [user?.id]);

  // Global event bridge so components deep in the tree (e.g., KPHomeMobile's
  // TopHeader avatar) can open the AccountDrawer without prop-drilling.
  useEffect(() => {
    const onDrawer = (e) => setDrawerOpen(Boolean(e?.detail?.open));
    window.addEventListener("kpp:drawer", onDrawer);
    return () => window.removeEventListener("kpp:drawer", onDrawer);
  }, []);

  // Warm the dashboard cache on first mount + on every PWA resume. See
  // useDashboardPrefetch for the full rationale.
  useDashboardPrefetch(user?.id);

  const onLogout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    // Dispatch to root-level overlay so it survives AppShell unmount during logout
    window.dispatchEvent(new CustomEvent("kpp:logout:start", {
      detail: { name: child?.name || user?.full_name },
    }));
    setTimeout(() => {
      logout();
      navigate("/");
    }, 900);
  };

  const navItems = [
    { to: "/home",       label: "Home",     icon: Home, end: true },
    { to: "/activities", label: "Today",    icon: CalendarCheck2 },
    { to: "/report",     label: "Report",   icon: Activity },
    { to: "/progress",   label: "Progress", icon: TrendingUp },
    { to: "/courses",    label: "Courses",  icon: GraduationCap },
    { to: "/disa",       label: "DISA",     icon: MessageCircle },
    { to: "/more",       label: "More",     icon: MoreHorizontal },
    { to: "/settings",   label: "Settings", icon: SettingsIcon },
  ];
  if (user?.role === "admin") navItems.push({ to: "/admin", label: "Admin", icon: Shield });

  const initial = (child?.name || user?.full_name || "?").trim().charAt(0).toUpperCase();

  // Sidebar collapse state — persisted so it survives navigation & refresh.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("kpp_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("kpp_sidebar_collapsed", next ? "1" : "0"); } catch { /* private mode */ }
      return next;
    });
  };

  return (
    <div className={`min-h-screen bg-kp-bg overflow-x-hidden relative ${sidebarCollapsed ? "md:pl-20" : "md:pl-64"} transition-[padding] duration-200`}>
      {/* LogoutOverlay is now rendered at root level (see RootLogoutOverlay in App.js)
          so it survives AppShell unmounting during the logout transition. */}
      <BrandedLoader visible={navigating && !loggingOut} title="Loading…" subtitle="Just a moment" />
      {impersonatedBy && (
        <div className="no-print bg-plum text-white text-xs sm:text-sm py-2 px-3 sm:px-4 flex flex-wrap items-center justify-center gap-2 sm:gap-3 sticky top-0 z-50" data-testid="impersonation-banner">
          <Eye className="w-4 h-4 shrink-0" />
          <span className="truncate">Viewing as {user?.full_name} ({user?.email})</span>
          <Button size="sm" variant="secondary" onClick={async () => { await stopImpersonation(); navigate("/admin"); }} data-testid="exit-impersonation-btn">Exit</Button>
        </div>
      )}
      <VerifyEmailBanner />
      {/* Desktop sidebar (hidden on mobile — mobile keeps its floating bell/avatar + bottom nav). */}
      <DesktopSidebar
        navItems={navItems}
        child={child}
        isPro={user?.plan === "pro"}
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />

      <main className="max-w-6xl mx-auto px-3 sm:px-6 md:px-8 pt-14 md:pt-8 pb-36 md:pb-12 relative">
        {/* Mobile floating avatar/bell removed — KP headers (`KPTopHeader`
            on tab pages, `KPBackHeader` on sub-pages) provide the same
            controls in the proper location. */}

        {/* Desktop: top-right bell + avatar. Clicking the avatar opens the
            same AccountDrawer used on mobile so the profile experience is
            identical on every viewport. */}
        <div className="hidden md:flex items-center gap-3 justify-end mb-4 no-print" data-testid="desktop-top-actions">
          <CommandPaletteTrigger />
          <NotificationsBell variant="desktop" />
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            data-testid="profile-avatar-btn-desktop"
            className="group flex items-center gap-1 shrink-0 active:scale-95 transition-transform"
          >
            <span className="w-10 h-10 rounded-full overflow-hidden shadow-soft border-2 border-teal-600/30 group-hover:border-teal-600 transition-all bg-gradient-to-br from-teal-600/15 to-teal-100 flex items-center justify-center text-inkdark font-heading text-lg font-bold">
              {child?.photo ? (
                <img src={child.photo} alt={child.name || "Child"} className="w-full h-full object-cover" />
              ) : (
                <span>{initial}</span>
              )}
            </span>
            <ChevronDown className="w-4 h-4 text-inkmid group-hover:text-inkdark transition-colors" strokeWidth={2.5} aria-hidden="true" />
          </button>
        </div>

        {/* key by pathname forces a re-mount on navigation, which retriggers
            the route-fade-in animation for a smooth tab-switch feel. */}
        <div key={pathname} className="route-fade-in">
          <Outlet />
        </div>
      </main>
      {/* Mobile bottom nav — matches Kiddo+ reference: white pill bar with a
          raised teal "Today" disc + tiny gold star. Hidden on full-screen DISA page. */}
      <nav
        className={`${pathname === "/disa" ? "hidden" : ""} md:hidden fixed bottom-0 inset-x-0 z-40 no-print kpp-bottom-nav pointer-events-none`}
        data-mobile-nav="true"
      >
        <div className="px-4 pb-1 pt-8 max-w-md mx-auto pointer-events-auto">
          <div className="relative bg-kp-surface rounded-[28px] shadow-[0_-4px_24px_rgba(17,24,39,0.10)] border border-kp-border/60 grid grid-cols-5 items-center h-[64px]">
            <MobileTab to="/home" label="Home" icon={Home} testid="mobile-nav-home" />
            <MobileTab to="/progress" label="Progress" icon={TrendingUp} testid="mobile-nav-progress" />
            {/* Center: elevated teal disc with calendar + tiny star. */}
            <NavLink
              to="/activities"
              end
              data-testid="today-tab-btn"
              aria-label="Today"
              className="absolute left-1/2 -translate-x-1/2 -top-5 flex flex-col items-center"
            >
              {({ isActive }) => (
                <>
                  <span className="relative w-[46px] h-[46px] rounded-full grid place-items-center bg-kp-primary text-white shadow-[0_4px_12px_rgba(13,148,136,0.40)] border-2 border-white dark:border-kp-surface transition-transform active:scale-95">
                    <CalendarCheck2 className="w-[19px] h-[19px]" strokeWidth={2.4} />
                    <Star
                      className="absolute top-1.5 right-2 w-2.5 h-2.5 text-[#FDE68A]"
                      strokeWidth={0}
                      fill="#FDE68A"
                    />
                  </span>
                  <span className={`text-[11px] mt-1.5 ${isActive ? "font-extrabold text-kp-primary" : "font-semibold text-kp-primary"}`}>Today</span>
                </>
              )}
            </NavLink>
            <span aria-hidden="true" />
            <MobileTab to="/courses" label="Courses" icon={BookOpen} testid="mobile-nav-courses" />
            <MobileTab to="/more" label="More" icon={LayoutGrid} testid="mobile-nav-more" />
          </div>
        </div>
      </nav>

      {/* Account drawer — matches parent-shared reference exactly */}
      <AccountDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={user}
        child={child}
        onLogout={onLogout}
      />
    </div>
  );
}


/**
 * Reusable side-tab for the pill-style mobile bottom nav. Active state
 * flips both icon and label to the mint palette without shifting layout.
 */
function MobileTab({ to, label, icon: Icon, testid }) {
  return (
    <NavLink
      to={to}
      end={to === "/activities"}
      data-testid={testid}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center py-2 text-[11px] font-semibold transition-colors ${
          isActive ? "text-kp-primary" : "text-kp-muted hover:text-kp-primary"
        }`
      }
    >
      <Icon className="w-[22px] h-[22px] mb-0.5" strokeWidth={2.2} />
      <span>{label}</span>
    </NavLink>
  );
}

/**
 * Persistent desktop sidebar — replaces the old top nav on tablet-landscape
 * and up (md+). Contains the wordmark logo, primary nav, a child switcher
 * card and a Go-Premium promo (hidden for Pro users). Mobile keeps its
 * bottom-nav; this sidebar is `hidden md:flex`.
 */
function DesktopSidebar({ navItems, child, isPro, collapsed, onToggle }) {
  const initial = (child?.name || "?").trim().charAt(0).toUpperCase();
  const ageLabel = (() => {
    const y = child?.age_years;
    const m = child?.age_months;
    if (typeof y === "number" && y > 0 && typeof m === "number" && m > 0) {
      return `${y} year${y === 1 ? "" : "s"}, ${m} mo`;
    }
    if (typeof y === "number" && y > 0) return `${y} year${y === 1 ? "" : "s"}`;
    if (typeof m === "number" && m > 0) return `${m} month${m === 1 ? "" : "s"}`;
    return "";
  })();

  // Reassessment eligibility: based on activity-days completed since last
  // assessment (not a fixed calendar date). The streak cache is warmed by
  // useDashboardPrefetch so this is always a zero-cost read.
  const streak = getCached("progress:streak");
  const reassessmentInfo = (() => {
    if (!streak) return null;
    const completed = streak.total_days_completed ?? 0;
    const target    = streak.target ?? 30;
    if (streak.unlock_assessment || completed >= target) {
      return { eligible: true, completed, target };
    }
    const remaining = Math.max(0, target - completed);
    const due       = new Date(Date.now() + remaining * 24 * 60 * 60 * 1000);
    const dateStr   = due.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    return { eligible: false, completed, target, remaining, dateStr };
  })();

  return (
    <aside
      className={`hidden md:flex md:fixed md:inset-y-0 md:left-0 md:flex-col md:z-40 bg-kp-surface border-r border-kp-border no-print transition-[width] duration-200 ${collapsed ? "md:w-20" : "md:w-64"}`}
      data-testid="desktop-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
    >
      {/* Wordmark + tagline · with collapse toggle */}
      <div className="relative flex items-center gap-2 px-4 pt-6 pb-4 shrink-0">
        <Link
          to="/home"
          className={`flex items-center gap-2 min-w-0 ${collapsed ? "justify-center flex-1" : ""}`}
          data-testid="sidebar-logo"
          aria-label="Go home"
        >
          <img src={LOGO} alt="" className="w-8 h-8 rounded-[22%] shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <img src={WORDMARK} alt="Kiddo+" className="h-6 w-auto dark:invert-0" />
              <p className="text-[10px] text-kp-ink2 mt-0.5 tracking-wide truncate">Every Child. Every Step.</p>
            </div>
          )}
        </Link>
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          title={collapsed ? "Expand menu" : "Collapse menu"}
          data-testid="sidebar-toggle"
          className={`absolute ${collapsed ? "-right-3 top-6" : "right-3 top-7"} w-7 h-7 rounded-lg bg-kp-surface border border-kp-border shadow-soft grid place-items-center text-inkmid hover:text-teal-700 hover:border-teal-600/40 hover:bg-teal-50 transition-colors z-10`}
        >
          {collapsed
            ? <ChevronsRight className="w-3.5 h-3.5" strokeWidth={2.4} />
            : <ChevronsLeft className="w-3.5 h-3.5" strokeWidth={2.4} />}
        </button>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Main">
        <ul className="space-y-0.5">
          {navItems.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                end={n.end}
                data-testid={`sidebar-nav-${n.label.toLowerCase()}`}
                title={collapsed ? n.label : undefined}
                aria-label={n.label}
                className={({ isActive }) =>
                  `relative flex items-center rounded-2xl transition-colors overflow-hidden ${
                    collapsed ? "justify-center px-0 py-4" : "gap-4 px-5 py-4"
                  } ${
                    isActive
                      ? "bg-teal-600/10 text-teal-700"
                      : "text-kp-ink hover:bg-teal-50/60 hover:text-teal-700"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Left active indicator bar */}
                    {isActive && !collapsed && (
                      <span className="absolute left-0 inset-y-2 w-[3.5px] rounded-r-full bg-teal-600" />
                    )}
                    <n.icon
                      className="shrink-0 w-[22px] h-[22px]"
                      strokeWidth={isActive ? 2.5 : 2.1}
                    />
                    {!collapsed && (
                      <span className={`text-[17px] ${isActive ? "font-extrabold" : "font-bold"}`}>
                        {n.label}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Child profile card — collapsed state shows only the avatar (routes to /child). */}
      {child && (
        collapsed ? (
          <Link
            to="/child"
            data-testid="sidebar-view-profile"
            aria-label={`View ${child.name || "child"} profile`}
            title={child.name || "View profile"}
            className="mx-auto mb-4 w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-teal-600/15 to-teal-100 border-2 border-teal-600/30 flex items-center justify-center text-inkdark font-heading text-base font-bold hover:border-teal-600/70 transition-colors"
          >
            {child.photo ? (
              <img src={child.photo} alt={child.name} className="w-full h-full object-cover" />
            ) : (
              <span>{initial}</span>
            )}
          </Link>
        ) : (
        <div className="mx-3 mb-5 rounded-2xl border border-kp-border bg-kp-surface p-3.5 shadow-soft overflow-hidden" data-testid="sidebar-child-card">
            <Link to="/child" data-testid="sidebar-view-profile" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <span className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-teal-600/15 to-teal-100 border-2 border-teal-600/30 flex items-center justify-center text-inkdark font-heading text-base font-bold shrink-0">
                {child.photo ? (
                  <img src={child.photo} alt={child.name} className="w-full h-full object-cover" />
                ) : (
                  <span>{initial}</span>
                )}
              </span>
              <p className="font-bold text-[16px] text-inkdark leading-tight flex-1 truncate">{child.name}</p>
              <ChevronRight className="w-4 h-4 text-inkmid shrink-0" strokeWidth={2.4} />
            </Link>
            {reassessmentInfo && (
              <div className="mt-3 pt-3 border-t border-kp-border/60">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-inkmid mb-1.5">Reassessment</p>
                {reassessmentInfo.eligible ? (
                  <>
                    <span className="inline-flex items-center gap-1 rounded-full bg-teal-600/12 text-teal-700 font-bold text-[11px] px-2.5 py-0.5 mb-1.5">
                      Ready now!
                    </span>
                    <div className="mt-1 h-1.5 rounded-full bg-kp-border overflow-hidden">
                      <div className="h-full w-full rounded-full bg-teal-500" />
                    </div>
                    <p className="text-[10px] text-teal-700 font-semibold mt-0.5">30/30 days complete</p>
                  </>
                ) : (
                  <>
                    <p className="text-[12px] font-semibold text-inkdark leading-tight">
                      {reassessmentInfo.dateStr}
                    </p>
                    <div className="mt-1.5 h-1.5 rounded-full bg-kp-border overflow-hidden">
                      <div
                        className="h-full rounded-full bg-teal-500 transition-all"
                        style={{ width: `${Math.round((reassessmentInfo.completed / reassessmentInfo.target) * 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-inkmid mt-0.5">
                      {reassessmentInfo.completed}/{reassessmentInfo.target} days completed
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )
      )}

      {/* Go Premium promo — only in expanded state (hidden for Pro users) */}
      {!isPro && !collapsed && (
        <Link
          to="/subscription"
          className="mx-4 mb-6 rounded-2xl bg-gradient-to-br from-gold/20 via-gold/10 to-cream border border-gold/30 p-3 hover:shadow-md transition-all"
          data-testid="sidebar-go-premium"
        >
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-gold shrink-0" strokeWidth={2.3} fill="currentColor" />
            <p className="font-heading text-sm text-inkdark leading-tight">Go Premium</p>
          </div>
          <p className="text-[11px] text-inkmid mt-1 leading-snug">
            Unlock full reports, expert insights and more activities.
          </p>
          <span className="mt-2 block w-full text-center bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-full py-2 transition-colors">
            Upgrade Now
          </span>
        </Link>
      )}
    </aside>
  );
}
